import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, Trash2 } from "lucide-react";
import api from "../lib/apiClient";
import DealCard from "../components/DealCard";
import { Seo } from "../components/Seo";
import { useLovedDeals } from "../context/LovedDealsContext";

export default function LovedDeals() {
  const navigate = useNavigate();
  const { refresh, toggle } = useLovedDeals();
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    setError(false);
    try {
      const { data } = await api.get("/loved-deals");
      setItems(Array.isArray(data) ? data : []);
      await refresh();
    } catch {
      setError(true);
      setItems([]);
    }
  }, [refresh]);

  useEffect(() => { load(); }, [load]);

  const remove = async (deal) => {
    const ok = await toggle(deal);
    if (ok) setItems((current) => current.filter((item) => item.id !== deal.id));
  };

  return (
    <div className="space-y-6 fade-up">
      <Seo title="Saved collections" description="Family collections you hearted and saved for later." path="/loved" noindex />
      <div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl text-ink flex items-center gap-2">
          <Heart className="text-brand fill-brand" aria-hidden="true" /> Saved collections
        </h1>
        <p className="text-ink-soft mt-1">Family collections you saved, kept together for an easy return.</p>
      </div>

      {items === null ? (
        <div className="bg-white rounded-3xl border border-line p-8 text-center text-ink-muted" role="status">Loading saved collections…</div>
      ) : error ? (
        <div className="bg-white rounded-3xl border border-line p-8 text-center" role="alert">
          <p className="font-bold text-ink">Saved collections could not be loaded.</p>
          <button onClick={load} className="mt-4 h-11 px-5 rounded-full bg-brand text-white font-bold text-sm">Try again</button>
        </div>
      ) : items.length === 0 ? (
        <div data-testid="loved-empty" className="bg-white rounded-3xl border border-line p-8 text-center">
          <p className="font-heading font-extrabold text-xl text-ink">You haven’t saved any collections yet</p>
          <p className="text-ink-soft mt-1">Tap the heart on any family collection to keep it here. You'll be able to save collections once our first ones arrive — nothing is available to shop yet.</p>
          <button onClick={() => navigate("/family-deals")} data-testid="loved-browse" className="mt-4 h-11 px-5 rounded-full bg-brand text-white font-bold text-sm inline-flex items-center gap-2">
            <ShoppingBag size={16} aria-hidden="true" /> Browse collections
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="loved-list">
          {items.map((deal) => (
            <div key={deal.id} className="space-y-2">
              {deal.availability_status && deal.availability_status !== "available" && (
                <p className="rounded-xl bg-gold/10 border border-gold/30 px-3 py-2 text-sm font-semibold text-ink" role="status">
                  {deal.availability_status === "sold_out" ? "Sold out at the last check — kept in your saved collections." : "This collection is no longer available — kept for your reference."}
                </p>
              )}
              <DealCard deal={deal} canUnlock />
              <button
                type="button"
                onClick={() => remove(deal)}
                data-testid={`loved-remove-${deal.id}`}
                className="w-full h-11 rounded-full border-2 border-line text-ink-soft font-bold text-sm flex items-center justify-center gap-2 hover:border-brand/40 hover:text-brand transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
              >
                <Trash2 size={16} aria-hidden="true" /> Remove from saved collections
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
